import * as React from 'react';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  Atom,
  withSystem,
  withMeta,
  TypeImplementation,
  Plugins,
} from '@packages/systems/wfdl';
import {getTagFromData, isTagBound} from '@packages/utilities/platform';
import {
  TList,
  TElement,
  TTypeConstructor,
  TEnum,
} from '@packages/systems/wfdl-ast';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';
import {defaultHeadingChildrenNodePojo} from '@packages/systems/core/constants/DomNodeDefaults';
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';
import {getChildren} from '@packages/utilities/data-utils';

const basicPlugin = Plugins.name('Basic');

export const HeadingTagType = TypeImplementation.Alias({
  type: TEnum({
    h1: '1',
    h2: '2',
    h3: '3',
    h4: '4',
    h5: '5',
    h6: '6',
  }),
  render: ({expr, dispatch}) => {
    return <ExpressionEditor expression={expr} dispatch={dispatch} />;
  },
  system: {
    label: 'Tag',
    displayName: 'Tag',
  },
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('HeadingTagProp'))
  ),
});

const Heading = Atom.createRender(
  ({attributes, classes, generateAttributes, data, innerHTML, renderChild}) => {
    // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
    const Tag = data.tag;
    return (
      <Tag {...generateAttributes(attributes, classes)}>
        {innerHTML || getChildren(data).map((child) => renderChild(child))}
      </Tag>
    );
  }
);

const metaByTag: {
  [key: string]: {
    displayName: string;
    icon: any;
  };
} = {
  h1: {
    displayName: 'Heading 1',
    icon: [basicPlugin, iconName('H1')],
  },
  h2: {
    displayName: 'Heading 2',
    icon: [basicPlugin, iconName('H2')],
  },
  h3: {
    displayName: 'Heading 3',
    icon: [basicPlugin, iconName('H3')],
  },
  h4: {
    displayName: 'Heading 4',
    icon: [basicPlugin, iconName('H4')],
  },
  h5: {
    displayName: 'Heading 5',
    icon: [basicPlugin, iconName('H5')],
  },
  h6: {
    displayName: 'Heading 6',
    icon: [basicPlugin, iconName('H6')],
  },
};

const config = createDevlinkAtom({
  showEmpty: true,
  icon: {
    type: 'Derived',
    getFromElement: (element) => {
      const byTag = metaByTag[getTagFromData(element)];

      if (byTag) {
        return byTag.icon;
      }
      if (isTagBound(element)) {
        return [basicPlugin, iconName('HeadingTagProp')];
      }
      return [basicPlugin, iconName('H1')];
    },
  },
  displayName: {
    type: 'Derived',
    defaultValue: 'Heading',
    getFromElement: (element) => {
      const byTag = metaByTag[getTagFromData(element)];

      if (byTag) {
        return byTag.displayName;
      }

      return 'Heading';
    },
  },
  render: Heading,
  fields: {
    tag: withSystem(
      {
        isBindable: true,
        isLocalizable: true,
        label: 'Tag',
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('HeadingTag')))
    ),
    children: withSystem(
      {
        isBindable: true,
        label: 'Text',
        defaultValue: defaultHeadingChildrenNodePojo,
      },
      TList(
        withMeta(
          {
            multiLine: false,
          },
          TElement
        )
      )
    ),
  },
});

export default config;
